/**
 * 📦 The chest, open — T135, D106, protocol 20.
 *
 * 🎯 **haugur, 2026-09-02:** *"lets talk about building chests for storing
 * stuff"*, and then *"27 for a single chest, double that for a double chest?"*
 *
 * ## 🔴 Why this is a copy of `hearth-panel.ts` and not a shared class
 *
 * ⚠️ **A real decision, and the wrong one is easy to defend.** The two files are
 * close enough that a `ContainerPanel` taking the slot count as a parameter
 * looks obvious — and it would have to carry, today, a fire line, two labelled
 * groups, a flame, per-slot item names, and *whether a slot has a fixed item at
 * all*. **Every one of those is a hearth fact.** The shared thing would be the
 * union of two screens rather than what they have in common.
 *
 * ✅ **What IS shared is real and already shared:** the marks (`hud-parts.ts`),
 * the arrangement (`hud-scene.ts`), and the four click rules. 👉 **When a third
 * container arrives — the búr, D71 — that is the moment to look again**, because
 * three is when a pattern is a pattern.
 *
 * ## 🔴 Nothing here decides what anything looks like
 *
 * The marks come from `hud-parts.ts` and the arrangement from `hud-scene.ts`.
 * This file is the plumbing: where the element sits, when it is rebuilt, and
 * what a click means.
 *
 * ## ⚠️ Nothing is carried by the SERVER
 *
 * Exactly as `pack.ts` and the hearth: an item picked up here never leaves its
 * slot as far as the server is concerned. The carrying is a drawing, and only
 * the finished move is sent — so closing mid-carry, or losing the connection,
 * puts nothing at risk.
 */

import {
  ELLS_PER_PIECE,
  INVENTORY_CARRIED,
  INVENTORY_ON_HAND,
  cooksTo,
  type LoomView,
  t,
  tAnyKey,
  type Stack,
} from '@lofi/shared';
import { hudScale, makeScene, type VariantName } from './hud-scene.ts';
import type { HearthMoveRequest } from './hearth-panel.ts';
import { slotContentFor } from './hud-cluster.ts';
import type { SlotContent } from './hud-parts.ts';

/** ✅ The variation haugur chose — the same one everything else draws in. */
const VARIANT: VariantName = 'scrim';

/** One end of a move: which container, and which slot in it. */
export interface ChestEnd {
  readonly where: 'chest' | 'player';
  readonly slot: number;
}

/**
 * Asked of the server when a carried stack is put down.
 *
 * ⚠️ **Both ends name their container.** Both start at slot 0, and a bare pair
 * of numbers would let the reader guess — which puts the right item in the wrong
 * box with nothing raised anywhere.
 */
export type ChestMoveRequest = (
  at: { x: number; y: number; z: number },
  from: ChestEnd,
  to: ChestEnd,
  count: number,
) => void;

/** What the player has picked up and is holding over the panel. */
/**
 * 🥫🔥 **One end of a move on this screen, and there are THREE places now** —
 * D121.
 *
 * 🔴 **`fire` is a búr's cooking fire**, whose slots are the HEARTH's — 0–1
 * fuel, 2–5 output, 6–7 what is to be cooked — because they travel as a
 * `move_hearth_item`. ⚠️ **A slot number is an identity on the wire and never a
 * position on the glass**, and on this screen the two genuinely disagree: the
 * cooking slots are drawn FIRST and numbered LAST.
 *
 * ⚠️ **One carry rather than two.** A second `carriedFire` beside `carried` was
 * written and thrown away: **two carries is two things to put back** when the
 * screen closes, and the one that was forgotten would leave a stack on the
 * cursor describing a state the server has never heard of.
 */
type PanelEnd = {
  /**
   * 🔴 **FOUR places on one screen now** — T222c.
   *
   * ⚠️ **`shelf` is not a fourth STORE**: it is a window onto the búr's own
   * food, showing only what this fire can cook. **Its slot number is the real
   * búr slot**, chosen when the shelf was built, so a move from it is an
   * ordinary store move and nothing has to be translated back.
   */
  readonly where: 'chest' | 'player' | 'fire' | 'shelf';
  readonly slot: number;
};

/**
 * 🥫🔥 Asked of the server when something moves between a búr's food store and
 * its own fire — T222c, protocol 34.
 */
export type BurMoveRequest = (
  at: { x: number; y: number; z: number },
  from: { where: 'store' | 'fire'; slot: number },
  to: { where: 'store' | 'fire'; slot: number },
  count: number,
) => void;

interface Carried {
  readonly from: PanelEnd;
  readonly itemKey: string;
  readonly count: number;
}

export class ChestPanel {
  private readonly svg: SVGSVGElement;
  private readonly body: SVGGElement;
  private readonly cursor: SVGGElement;
  private readonly onMove: ChestMoveRequest;
  /** 🏠 How many slots the open container has — the screen grows to it. */
  private capacity = 27;
  /**
   * 🥫🔥 **This container's fire, as eight slots** — D121, protocol 33.
   *
   * 🔴 **Empty means NO FIRE**, which is every chest and every skemma. **Only a
   * búr has one**, and a container without one draws exactly the page it always
   * drew — no tabs at all.
   */
  private fire: readonly (string | null)[] = [];
  private fireCounts: readonly number[] = [];
  private fireLit = false;
  /** 🧵 A vefstofa's spinning — protocol 41. `null` for every other container. */
  private spin: 'spinning' | 'idle' | 'full' | null = null;
  /** 🧵 A loom's weaving — protocol 53, D136. `null` for every other container. */
  private loom: LoomView | null = null;
  private fireHours = 0;
  /** 🗂️ Which tab is open. Meaningless without a fire. */
  private tab: 'food' | 'fire' = 'food';
  /**
   * 🪧 **What the container calls itself** — protocol 24, an `en.json` key.
   *
   * 🔴 **This used to be worked out from {@link capacity}**, and the guess was
   * *"more than 27 slots means it is the búr"* — **which made the skemma, at 54
   * slots, announce itself as the food store.** 🎯 **haugur, 2026-09-04:** *"the
   * menu for skemma, it says Búr."* ⚠️ **The size was never the question**, and
   * the next 54-slot container would have been wrong too.
   */
  private title: string | null = null;

  /** The drawing's own units — see {@link scale}. */
  private width = 0;
  private height = 0;
  private rawW = 0;
  private rawH = 0;
  /** 🔍 How much bigger than the design frame this screen draws — T170. */
  private scale = 1;

  private open = false;
  /** Which chest is open. `null` means none, and nothing draws. */
  private at: { x: number; y: number; z: number } | null = null;

  /**
   * 📦 **What is in the chest — full stacks, straight from the server.**
   *
   * 🔴 **Not counts**, and that is the one thing this panel got right that the
   * hearth's had to be corrected for. A hearth slot's identity could be guessed
   * from its number, so only counts were sent — and the panel guessed birch
   * while the fire burned driftwood. **A chest slot can hold anything**, so
   * there is nothing to guess from and the item key travels with the count.
   */
  private chest: readonly Stack[] = [];

  private stacks: readonly Stack[] = [];
  private selected = 0;
  private hover: PanelEnd | null = null;
  private carried: Carried | null = null;
  /**
   * 🔥 **How a move involving the fire is sent** — D121, protocol 33.
   *
   * 🔴 **It is the HEARTH's message, not a new one.** A búr's fire is a hearth,
   * and the server reads the block at the named position to decide which rules
   * apply — so **a client cannot choose to be governed by the hearth's rules
   * instead of the búr's**, which is what keeps charcoal out of a búr (D71).
   */
  private readonly onFireMove: HearthMoveRequest;
  /**
   * 🥫🔥 **How a move between the búr's STORE and its FIRE is sent** — T222c,
   * protocol 34.
   *
   * 🔴 **Its own message because the pack is at neither end**, which is true of
   * no other move on this screen.
   */
  private readonly onStoreFireMove: BurMoveRequest;

  constructor(
    onMove: ChestMoveRequest,
    onFireMove: HearthMoveRequest = () => undefined,
    onStoreFireMove: BurMoveRequest = () => undefined,
  ) {
    this.onMove = onMove;
    this.onFireMove = onFireMove;
    this.onStoreFireMove = onStoreFireMove;

    this.svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    /* ⚠️ The pack's own class, so it inherits the overlay's position and its
       `pointer-events: auto`. Three screens of the same kind in the same place. */
    this.svg.setAttribute('class', 'packpanel');
    this.svg.setAttribute('aria-hidden', 'true');
    this.svg.style.display = 'none';

    this.body = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.cursor = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    this.svg.append(this.body, this.cursor);
    document.body.appendChild(this.svg);

    /* 🔴 A ResizeObserver, never a window `resize` listener — an `<svg>`
       measured before layout still has its default 300 × 150, nothing throws,
       and the whole screen comes out at the wrong scale looking deliberate. */
    new ResizeObserver(() => {
      this.measure();
    }).observe(this.svg);
    this.measure();

    this.svg.addEventListener('mousemove', (e) => this.onMouseMove(e));
    this.svg.addEventListener('mousedown', (e) => this.onMouseDown(e));
    this.svg.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /** 🥫🔥 Does the open container have a fire — a búr — so its screen must be polled? */
  get hasFire(): boolean {
    return this.fire.length > 0;
  }

  /** 🔁 Must the screen keep asking? A búr with a fire, or a vefstofa spinning — D127. */
  get polls(): boolean {
    return (
      this.hasFire ||
      this.spin === 'spinning' ||
      this.loom?.doing === 'warping' ||
      this.loom?.doing === 'weaving'
    );
  }

  get isOpen(): boolean {
    return this.open;
  }

  /** Which chest is open, so the caller can tell whether a message is for it. */
  get openAt(): { x: number; y: number; z: number } | null {
    return this.at;
  }

  setOpen(open: boolean): void {
    if (open === this.open) return;
    this.open = open;
    /* ⚠️ Closing puts a carried stack back where it came from. Nothing was ever
       sent, so there is nothing to undo — but a screen that reopened with a
       stack still on the cursor would be describing a state the server has
       never heard of. */
    this.carried = null;
    this.hover = null;
    if (!open) this.at = null;
    this.svg.style.display = open ? 'block' : 'none';
    this.draw();
  }

  /** What the server says this chest holds — protocol 20. */
  setContents(
    at: { x: number; y: number; z: number },
    chest: readonly Stack[],
    /** 🏠 27 for a chest, 54 for a storehouse door — protocol 23. */
    capacity = 27,
    /**
     * 🪧 **What this container is called** — protocol 24, an `en.json` key.
     *
     * ⚠️ **`null` from a server too old to say it**, and we fall back to the old
     * guess below.
     */
    title: string | null = null,
    /**
     * 🥫🔥 **The container's fire, or `null`** — protocol 33, D121.
     *
     * ⚠️ **`null` from a server that has not caught up**, and from every
     * container that has no fire — **which is the same drawing either way**: the
     * page this screen has always had.
     */
    fire: {
      readonly slots: readonly number[];
      readonly slotItems: readonly (string | null)[];
      readonly lit: boolean;
      readonly hoursLeft: number;
    } | null = null,
    /** 🧵 A vefstofa's spinning — protocol 41, D127. */
    spin: 'spinning' | 'idle' | 'full' | null = null,
    /** 🧵 A loom's weaving — protocol 53, D136. */
    loom: LoomView | null = null,
  ): void {
    this.at = at;
    this.spin = spin;
    this.loom = loom;
    this.chest = chest;
    this.capacity = capacity;
    this.title = title;
    this.fireCounts = fire?.slots ?? [];
    this.fire = fire?.slotItems ?? [];
    this.fireLit = fire?.lit ?? false;
    this.fireHours = fire?.hoursLeft ?? 0;
    /* 🔴 A container with no fire cannot be showing its Fire tab. A stale tab
       would draw an empty fire over a búr the server has stopped describing. */
    if (this.fire.length === 0) this.tab = 'food';
    this.draw();
  }

  /** What the server says is in the pack — protocol 9. */
  setInventory(stacks: readonly Stack[], selected: number): void {
    this.stacks = stacks;
    this.selected = selected;
    this.draw();
  }

  private measure(): void {
    const w = this.svg.clientWidth;
    const h = this.svg.clientHeight;
    if (w === this.rawW && h === this.rawH) return;
    if (w <= 0 || h <= 0) return;
    this.rawW = w;
    this.rawH = h;
    this.scale = hudScale(w, h);
    this.width = w / this.scale;
    this.height = h / this.scale;
    this.svg.setAttribute('viewBox', `0 0 ${String(this.width)} ${String(this.height)}`);
    this.draw();
  }

  private scene(): ReturnType<typeof makeScene> {
    /* 🔴 `TOPBAR: 0` — this element already sits below the status bar, so the
       bar covers none of it. Applying the 34 here twice leaves an undimmed band
       across the top that looks entirely deliberate. */
    return makeScene({ SW: this.width, SH: this.height, TOPBAR: 0 });
  }

  /**
   * Where the mouse is, in the drawing's own coordinates.
   *
   * 🔴 **Divided by the zoom** — T170. One unit of this drawing is
   * {@link scale} pixels on the glass, and without the division a click lands
   * short of where the eye says, by more the further from the corner it is.
   */
  private pointAt(e: MouseEvent): { x: number; y: number } {
    const box = this.svg.getBoundingClientRect();
    return { x: (e.clientX - box.left) / this.scale, y: (e.clientY - box.top) / this.scale };
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.open) return;
    const { x, y } = this.pointAt(e);
    if (this.carried) this.cursor.setAttribute('transform', `translate(${String(x)},${String(y)})`);
    /* 🔴 THE SAME QUESTION THE CLICK ASKS — #734. Asking a different one is
       what put the white box three quarters of a slot below the mouse. */
    const over = this.endAt(x, y);
    /* ⚠️ Redrawn only when the slot under the mouse CHANGES. At mouse-move
       rates a rebuild per event is the fault `hud-cluster.ts` warns about. */
    if (same(over, this.hover)) return;
    this.hover = over;
    this.draw();
  }

  /**
   * 🔴 **WHAT IS UNDER THE MOUSE — asked ONCE, by the click AND by the hover.**
   *
   * 🎯 **haugur, 2026-09-22, issue #734:** *"See how the white box is 3/4 below
   * the box it is supose to higlight"* — and then: *"we have had this bug before
   * with other menus but you fixed it."* **He is right.**
   *
   * 🔴 **`06_Traps.md` carries the earlier one, 2026-09-08:** the hover
   * highlight was drawn with the default capacity while the grid and the hit
   * test used a storehouse's real 54 — 🎯 *"in skemma menu, the highlight is 2.5
   * tiles below mouse."* **Same screen family, same symptom, a different
   * cause**, and that is what says the CLASS was never closed.
   *
   * ⚠️ **This time: the click learned about three places and the hover did
   * not.** `onMouseMove` went on asking `chestSlotAt`, so on the Fire tab it
   * answered about a grid that is not even drawn — and the white box landed
   * wherever that grid's slot happened to be.
   *
   * ✅ **So there is one function now**, and a fourth place cannot be added to
   * one path and forgotten in the other. 👉 ***Two code paths answering one
   * question is how they drift***, which this file's neighbours say about
   * containers and moves and is just as true of a mouse.
   */
  /** 🧵 A loom's screen is drawn chest-tall, for its picture; every hit test must agree. */
  private get rowsAtLeast(): number {
    return this.loom === null ? 1 : 3;
  }

  /** 🧵 The loom's half of the screen, in words — protocol 8's rule: the server sends numbers. */
  private loomPicture(): Parameters<ReturnType<typeof makeScene>['chestPanel']>[14] {
    const loom = this.loom;
    if (loom === null) return null;
    const line =
      loom.doing === 'weaving'
        ? t('vefstadur.weaving', { ell: String(loom.ells + 1) })
        : t(`vefstadur.${loom.doing}`);
    const woven = Math.min(
      1,
      (loom.ells + (loom.doing === 'weaving' ? loom.part : 0)) / ELLS_PER_PIECE,
    );
    return {
      doing: loom.doing,
      part: loom.part,
      woven,
      line,
      warp: t('vefstadur.slot.warp'),
      weft: t('vefstadur.slot.weft'),
      cloth: t('vefstadur.slot.cloth'),
    };
  }

  private endAt(x: number, y: number): PanelEnd | null {
    const scene = this.scene();
    if (this.tab !== 'fire') return scene.chestSlotAt(x, y, this.capacity, this.rowsAtLeast);

    /* 🔥 The fire first: its slots and the container's grid occupy the same part
       of the panel, and only one of them is drawn at a time. */
    const f = scene.containerFireSlotAt(x, y, this.capacity);
    if (f !== null) return { where: 'fire', slot: f };

    /**
     * 🥫🍲 **The shelf — T222c.** What is in the búr that this fire can cook.
     *
     * 🔴 **Its index is a POSITION on the shelf; the slot it maps to is a real
     * búr slot.** ⚠️ The translation happens here and nowhere else, so nothing
     * downstream has to know the shelf exists.
     */
    const sh = scene.containerShelfAt(x, y, this.capacity);
    if (sh !== null) {
      /* 🔴 **The INDEX on the shelf, not the búr slot it stands for.**
         ⚠️ **The whole panel works in what-you-SEE space** and translates to
         the container's own numbering exactly once, in `asBurEnd`, at the
         moment it speaks to the server. **Translating in two directions in two
         places is how a drawing and a hit test drift apart** — which is #734,
         the bug this method was written to end. */
      return this.shelfStacks().at(sh) === undefined ? null : { where: 'shelf', slot: sh };
    }

    /* 🎒 The pack and hand rows are drawn on BOTH tabs. ⚠️ A `chest` answer
       here would be a grid that is not on screen. */
    const c = scene.chestSlotAt(x, y, this.capacity, this.rowsAtLeast);
    return c !== null && c.where === 'player' ? c : null;
  }

  /**
   * 🖱️ What a click means. **The same four rules as the pack and the hearth**,
   * so one screen does not teach a habit another punishes.
   *
   * | | |
   * |---|---|
   * | **Left-click a slot with something in it** | Pick the whole stack up |
   * | **Right-click** | Pick up **half**, rounded up |
   * | **Click another slot while carrying** | Put it there. **The only thing that sends a message** |
   * | **Click the same slot, or off the panel** | Put it back. Nothing was sent |
   *
   * ⚠️ **Whether the move is ALLOWED is not decided here.** 🥫 Food being
   * refused, a full slot, a stack you do not own — those are the server's
   * answers, and it replies with both halves either way. **Two copies of one
   * rule is how the screen and the world start disagreeing.**
   */
  private onMouseDown(e: MouseEvent): void {
    if (!this.open || this.at === null) return;
    if (e.button !== 0 && e.button !== 2) return;
    e.preventDefault();

    const { x, y } = this.pointAt(e);
    const scene = this.scene();

    /**
     * 🗂️ **The Food/Fire tabs come first** — D121.
     *
     * ⚠️ **First, because they sit above the grid** and a click that fell
     * through would land on a slot. 🔴 **A tab click while CARRYING is ignored
     * rather than dropped**: switching pages with a stack on the cursor would
     * leave it nowhere, which is the rule every screen here already follows.
     */
    if (this.fire.length > 0 && this.carried === null) {
      const which = scene.containerTabAt(x, y, this.capacity, [
        t('bur.tab.food'),
        t('bur.tab.fire'),
      ]);
      if (which !== null) {
        const want = which === 1 ? 'fire' : 'food';
        if (want !== this.tab) {
          this.tab = want;
          this.hover = null;
          this.draw();
        }
        return;
      }
    }

    const target = this.endAt(x, y);

    if (this.carried === null) {
      if (target === null) return;
      const count = this.countIn(target);
      if (count <= 0) return;
      const itemKey = this.itemIn(target);
      if (itemKey === undefined) return;
      /* Half, rounded UP — right-clicking a single item picks it up rather than
         picking up nothing, which looks like a dead click. */
      this.carried = {
        from: target,
        itemKey,
        count: e.button === 2 ? Math.ceil(count / 2) : count,
      };
      this.cursor.setAttribute('transform', `translate(${String(x)},${String(y)})`);
      this.draw();
      return;
    }

    const { from, count } = this.carried;
    this.carried = null;

    /* Off the panel, or back where it came from: put it down, send nothing. */
    if (target === null || same(target, from)) {
      this.draw();
      return;
    }

    this.draw();

    /**
     * 🔴 **Which message this is depends on whether a FIRE is involved** —
     * D121, protocol 33.
     *
     * 🔥 **A move touching the fire is the HEARTH's message**, because a búr's
     * fire is a hearth and the server picks the rules from the block it finds
     * there. 📦 **Anything else is the container's.**
     *
     * ⚠️ **And a move with both ends in the player's own pack is NEITHER** —
     * that is `move_item`'s job, **which is T247**, the bug haugur found today:
     * sending it as a container move got it silently refused with nothing said.
     * 🔴 **The routing lives in `main.ts` for the container message and must
     * not be re-decided here**, so this hands both ends over unchanged.
     */
    /**
     * 🥫🔥 **The búr's STORE and its FIRE reaching each other is its own
     * message** — T222c, protocol 34 — **because the pack is at neither end.**
     * 🔴 **Checked BEFORE the plain fire case**, or a shelf-to-fire drag would
     * be sent as a pack-to-fire one and refused for naming a pack slot that
     * holds something else entirely.
     */
    const involvesShelf = from.where === 'shelf' || target.where === 'shelf';
    if (involvesShelf) {
      const shelf = this.shelfStacks();
      this.onStoreFireMove(this.at, asBurEnd(from, shelf), asBurEnd(target, shelf), count);
      return;
    }
    if (from.where === 'fire' || target.where === 'fire') {
      this.onFireMove(this.at, asHearthEnd(from), asHearthEnd(target), count);
      return;
    }
    this.onMove(this.at, from as ChestEnd, target as ChestEnd, count);
  }

  /**
   * 🥫🍲 **What the shelf shows: the búr's own food that this fire can cook** —
   * T222c.
   *
   * 🔴 **Each entry keeps its REAL búr slot**, so a drag off the shelf is an
   * ordinary store move with nothing to translate back. ⚠️ **A pooled view
   * would have had to guess which of the 54 to take from**, and the guess is
   * where a screen and a world start disagreeing.
   *
   * ⚠️ **Only what has a `cooksTo`.** Söl and berries keep in a búr and cannot
   * be cooked, so putting them on a shelf labelled *drag up to cook* would be
   * offering something the fire will refuse.
   *
   * ⚠️ **And it is capped at what is DRAWN.** 🔴 If a búr ever held more kinds
   * of cookable food than the shelf has squares, the rest would be invisible —
   * **which is D122's whole lesson.** There are four fish in the game and
   * twenty-seven squares, so it cannot bite today; `chest-panel.test.ts` is
   * where it would be caught if that ever changed.
   */
  private shelfStacks(): readonly Stack[] {
    return this.chest
      .filter((s) => cooksTo(s.itemKey) !== undefined)
      .slice(0, this.scene().containerShelfCapacity());
  }

  /** The stack in one end, or `undefined`. ⚠️ The fire is not stacks. */
  private stackIn(end: PanelEnd): Stack | undefined {
    if (end.where === 'fire') return undefined;
    /* 🥫 The shelf is INDEXED — its `slot` is a position on the shelf, and the
       stack it points at carries its own real búr slot. */
    if (end.where === 'shelf') return this.shelfStacks().at(end.slot);
    const bag = end.where === 'chest' ? this.chest : this.stacks;
    return bag.find((s) => s.slot === end.slot);
  }

  /** How many are in one end, taking the carried stack out of its source. */
  private countIn(end: PanelEnd): number {
    const held = this.carried !== null && same(this.carried.from, end) ? this.carried.count : 0;
    /* 🔥 The fire keeps counts and names in two arrays, the way the hearth
       sends them — one per slot, not a list of stacks. */
    if (end.where === 'fire') return (this.fireCounts.at(end.slot) ?? 0) - held;
    return (this.stackIn(end)?.count ?? 0) - held;
  }

  /** What item one end holds. */
  private itemIn(end: PanelEnd): string | undefined {
    /* 🔥 The fire names its slots per slot, the way the hearth sends them. */
    if (end.where === 'fire') return this.fire.at(end.slot) ?? undefined;
    return this.stackIn(end)?.itemKey;
  }

  private draw(): void {
    if (!this.open || this.width <= 0 || this.at === null) return;
    const scene = this.scene();

    /* 📦 The chest's 27. ⚠️ **Filled with empties first**, so slot 20 is drawn in
       position 20 even when nothing below it is used — an array with holes would
       shuffle everything left, which looks like the items moved. */
    const chest: SlotContent[] = Array.from({ length: this.capacity }, () => ({}));
    for (const stack of this.chest) {
      if (stack.slot < 0 || stack.slot >= this.capacity) continue;
      const count = this.countIn({ where: 'chest', slot: stack.slot });
      if (count <= 0) continue;
      chest[stack.slot] = slotContentFor({ ...stack, count });
    }

    /**
     * 🔥 **The fire's eight, as pictures** — D121.
     *
     * ⚠️ **Built the same way the hearth builds its own**, from a count and a
     * name per slot — **not from stacks**. A count with no name beside it is
     * the fault `woodItem` was added to fix, and this is the third place that
     * rule now applies.
     */
    const fire: SlotContent[] =
      this.fire.length === 0
        ? []
        : this.fire.map((itemKey, i) => {
            const count = this.countIn({ where: 'fire', slot: i });
            if (count <= 0 || itemKey === null) return {};
            return slotContentFor({ slot: i, itemKey, count });
          });

    /* 🎒 The player's own things, in the two groups this screen draws. */
    const hand: SlotContent[] = Array.from({ length: INVENTORY_ON_HAND }, () => ({}));
    const pack: SlotContent[] = Array.from(
      { length: INVENTORY_CARRIED - INVENTORY_ON_HAND },
      () => ({}),
    );
    for (const stack of this.stacks) {
      /* ⚠️ 34-37 are what you are WEARING and this screen does not draw them.
         Letting them through would index past the end of the pack array. */
      if (stack.slot < 0 || stack.slot >= INVENTORY_CARRIED) continue;
      const count = this.countIn({ where: 'player', slot: stack.slot });
      if (count <= 0) continue;
      const content = slotContentFor({ ...stack, count });
      if (stack.slot < INVENTORY_ON_HAND) hand[stack.slot] = content;
      else pack[stack.slot - INVENTORY_ON_HAND] = content;
    }

    replace(
      this.body,
      scene.chestPanel(
        VARIANT,
        chest,
        pack,
        hand,
        this.selected,
        this.hover,
        {
          /* 🔴 Standards §8b — every sentence a player reads lives in `en.json`.
             The drawing carries English defaults so a sample sheet can be made
             without the loader; the GAME always hands the words down. */
          /* 🪧 The container's own name, protocol 24. **`tAnyKey` because it
             arrived over the network** — the same reason protocol 8's refusal
             keys use it, and an unknown key shows itself rather than blanking.
             ⚠️ The fallback is the old capacity guess, kept for a server too old
             to send one: it is wrong for a skemma, and being wrong the way we
             already were beats a panel with no name on it at all. */
          chest: tAnyKey(this.title ?? (this.capacity > 27 ? 'bur.title' : 'chest.title')),
          title: t('pack.title'),
          onHand: t('pack.onHand'),
          close: t('pack.close'),
          /* 🗂️🔥 D121. Only drawn when there is a fire to draw. */
          tabFood: t('bur.tab.food'),
          tabFire: t('bur.tab.fire'),
          toCook: t('hearth.toCook'),
          wood: t('hearth.wood'),
          /* 🔴 NOT the hearth's "Cooked · charcoal" — a búr's fire makes no
             charcoal (D71), and a label naming a thing that can never appear
             is the fault this repo keeps paying for. */
          charcoal: t('bur.fire.output'),
        },
        this.capacity,
        fire,
        this.tab,
        /**
         * 🔥 **What the fire is doing, as a finished sentence.**
         *
         * 🔴 **Written here, never by the server** — protocol 8's rule: the
         * server does not know what language this player reads, so it sends
         * `lit` and a number of game hours and this turns them into words.
         * ⚠️ **The hearth says the same thing in the same place**, and says it
         * from the same two fields.
         */
        this.fire.length === 0
          ? null
          : this.fireLit
            ? t('bur.fire.burning', { hours: String(Math.max(1, Math.round(this.fireHours))) })
            : t('bur.fire.out'),
        /* 🥫🍲 The shelf — T222c. Each square keeps its real búr slot, so a
           drag off it needs no translation anywhere downstream. */
        this.tab === 'fire'
          ? this.shelfStacks().map((stack, i) =>
              /* 🥫 `i` — the position on the shelf, not the búr slot. The whole
                 panel works in what-you-see space; see `asBurEnd`. */
              slotContentFor({ ...stack, count: this.countIn({ where: 'shelf', slot: i }) }),
            )
          : [],
        this.tab === 'fire' ? t('bur.shelf') : null,
        /* 🧵 D127 — what the vefstofa is doing, in words. */
        this.spin === null
          ? null
          : t(
              this.spin === 'spinning'
                ? 'vefstofa.spinning'
                : this.spin === 'full'
                  ? 'vefstofa.full'
                  : 'vefstofa.idle',
            ),
        /* 🧵 D136 — a loom's names, sentence, bar and picture. */
        this.loomPicture(),
      ),
    );
    this.drawCursor();
  }

  /**
   * The stack riding the cursor.
   *
   * 🔴 **Its own group so it can move without a redraw** — following the mouse
   * by rebuilding the panel would re-parse an icon sixty times a second.
   */
  private drawCursor(): void {
    if (this.carried === null) {
      replace(this.cursor, '');
      return;
    }
    const scene = this.scene();
    const half = scene.SLOT / 2;
    const content = slotContentFor({
      slot: 0,
      itemKey: this.carried.itemKey,
      count: this.carried.count,
    });
    replace(
      this.cursor,
      `<g transform="translate(${String(-half)},${String(-half)})" pointer-events="none">` +
        scene.P.slot('carved', 0, 0, content) +
        `</g>`,
    );
  }
}

/**
 * Put a fragment of SVG markup inside an element.
 *
 * ⚠️ **Parsed, not assigned to `innerHTML`** — the same rule and the same
 * reasoning as `pack.ts` and `hearth-panel.ts`: everything drawn here is ours
 * today, and the moment an item name comes from somewhere else this is the line
 * that would have had to change.
 */
function replace(target: SVGGElement, markup: string): void {
  if (markup === '') {
    target.replaceChildren();
    return;
  }
  const parsed = new DOMParser().parseFromString(
    `<svg xmlns="http://www.w3.org/2000/svg">${markup}</svg>`,
    'image/svg+xml',
  );
  const root = parsed.documentElement;
  if (root.nodeName === 'parsererror' || root.getElementsByTagName('parsererror').length > 0) {
    console.error('Chest: could not parse the drawing', root.textContent);
    return;
  }
  target.replaceChildren(...Array.from(root.childNodes).map((n) => document.importNode(n, true)));
}

/** Two ends are the same place only when both halves agree. */
/**
 * 🔥 **A panel end as the HEARTH's wire calls it** — D121, protocol 33.
 *
 * 🔴 **`fire` becomes `hearth`, and `player` stays `player`.** The búr's fire
 * travels as a `move_hearth_item` because **it is a hearth** — and the server
 * reads the block at the named position to decide which rules govern it, so a
 * client cannot choose the hearth's rules for a búr. ⚠️ **`chest` can never
 * reach here**: a move touching the fire has no container end, and the caller
 * has already split the two cases.
 */
function asHearthEnd(end: PanelEnd): { where: 'hearth' | 'player'; slot: number } {
  return { where: end.where === 'player' ? 'player' : 'hearth', slot: end.slot };
}

/**
 * 🥫🔥 **A panel end as protocol 34 calls it** — T222c.
 *
 * 🔴 **`shelf` becomes `store`**, because the shelf IS the store — it shows a
 * chosen few of its slots and carries their real numbers. ⚠️ **`player` can
 * never reach here**: a move touching the shelf has the pack at neither end,
 * and the caller has already split that case off.
 */
function asBurEnd(
  end: PanelEnd,
  shelf: readonly Stack[],
): { where: 'store' | 'fire'; slot: number } {
  if (end.where === 'fire') return { where: 'fire', slot: end.slot };
  /* 🔴 **THE ONE PLACE A SHELF POSITION BECOMES A BÚR SLOT.** Everything above
     works in what-you-see space; this is the single translation, at the moment
     the server is spoken to. ⚠️ **A second one anywhere else is #734 again.** */
  return { where: 'store', slot: shelf.at(end.slot)?.slot ?? -1 };
}

function same(a: PanelEnd | null, b: PanelEnd | null): boolean {
  if (a === null || b === null) return a === b;
  return a.where === b.where && a.slot === b.slot;
}
